import { Component } from '@theme/component';
import { productHandleFromUrl } from '@theme/utilities';

/**
 * A size grid, and the size guide drawer that explains it.
 *
 * Built from one style, which it is given rather than resolving: `style` is set by the
 * selection above it, from the style grid beside it. That is what keeps two pairs, or two
 * tabs, from ever showing each other's sizes.
 *
 * The chips are cloned from the `sizeChip` template rather than written here, so their
 * classes, ARIA and bindings have one definition — see product-builder-size-button.liquid.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [sizeGrid] - Where the chips are built.
 * @property {HTMLTemplateElement} [sizeChip] - Chip template, cloned per size.
 * @property {HTMLButtonElement} [sizeGuideLink] - "View size guide" link.
 * @property {HTMLElement} [sizeGuideDialog] - This block's own drawer.
 * @property {HTMLElement} [sizeGuide] - The guide inside that drawer.
 * @property {HTMLElement} [defaultStyle] - Hidden tile for the block's default style.
 * @property {HTMLElement} [sizingNote] - Sizing recommendation text for the selected style.
 *
 * @extends {Component<Refs>}
 */
class ProductBuilderSizes extends Component {
  /** @type {HTMLElement | null} The style whose ladder is on screen. */
  #style = null;

  /** @type {string | null} A size named before its chip existed, from the url. */
  #pending = null;

  connectedCallback() {
    super.connectedCallback();

    // The guide reports whether that style has one; the link waits on that.
    this.addEventListener('size-guide:loaded', this.#handleGuideLoaded);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('size-guide:loaded', this.#handleGuideLoaded);
  }

  /** @returns {string | null} The size picked here. */
  get size() {
    const chip = this.#chips.find((button) => button.classList.contains('is-selected'));

    return chip?.dataset.size ?? null;
  }

  /**
   * @returns {HTMLElement | null} The style to open on, when the block names one. The size
   * guide follows `style`, so naming it here settles both.
   */
  get defaultStyle() {
    return this.refs.defaultStyle ?? null;
  }

  /** @returns {HTMLElement | null} The style this grid is built from. */
  get style() {
    return this.#style;
  }

  /**
   * Point this grid at a style. Rebuilding keeps the shopper's size when the new style
   * offers it too, because comparing two styles in one size is the common move.
   *
   * @param {HTMLElement | null} tile - A style tile, or null to empty the grid.
   */
  set style(tile) {
    const previous = this.#style?.dataset.style ?? null;
    const next = tile?.dataset.style ?? null;
    this.#style = tile;

    this.#updateGuide();
    this.#updateSizingNote(tile);

    // Same style, same ladder: rebuilding would drop a selection for no reason.
    if (previous === next) return;

    const wanted = this.#pending ?? (this.#keepSize ? this.size : null);
    this.#pending = null;
    this.#keepSize = true;

    this.#build(tile);

    if (wanted) this.selectByLabel(wanted);
  }

  /** @type {boolean} Whether the next style keeps the size already picked. */
  #keepSize = true;

  /**
   * Take the next style as a fresh start, rather than a change of mind within one ladder.
   *
   * Two categories measure differently — a men's L is not a women's L — so a size chosen for
   * one says nothing about the other, and carrying it over would put a number beside a garment
   * it was never picked for.
   */
  forgetSize() {
    this.#keepSize = false;
  }

  /**
   * Pick a size by its label, for a `?size=` restored from the url.
   *
   * Held until a grid exists when it arrives first, which it does whenever the url is
   * applied before a style has been resolved.
   *
   * @param {string} size
   * @returns {boolean} Whether this grid offered it.
   */
  selectByLabel(size) {
    const chip = this.#chips.find((button) => button.dataset.size === size);

    if (!chip) {
      this.#pending = size;
      return false;
    }

    this.#apply(chip);

    return true;
  }

  /**
   * Called by the on:click binding on each chip.
   * @param {MouseEvent} event
   */
  selectSize(event) {
    const chip = /** @type {HTMLElement} */ (event.target);

    // A size the selected type cannot honour is greyed out but stays in the DOM.
    if (chip.getAttribute('aria-disabled') === 'true') return;

    this.#apply(chip);
  }

  /** Open the drawer beside this grid. Its guide was fetched when the style was set. */
  openSizeGuide() {
    const dialog = /** @type {any} */ (this.refs.sizeGuideDialog);

    if (typeof dialog?.showDialog === 'function') dialog.showDialog();
  }

  /** @returns {HTMLElement[]} */
  get #chips() {
    return /** @type {HTMLElement[]} */ ([...(this.refs.sizeGrid?.children ?? [])]);
  }

  /** @param {HTMLElement} chip */
  #apply(chip) {
    for (const sibling of this.#chips) {
      const selected = sibling === chip;
      sibling.classList.toggle('is-selected', selected);
      sibling.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }

    this.dispatchEvent(
      new CustomEvent('product-builder-sizes:change', { bubbles: true, detail: { size: this.size } })
    );
  }

  /**
   * Rebuild the chips from a style's own ladder.
   * @param {HTMLElement | null} tile
   */
  #build(tile) {
    const grid = this.refs.sizeGrid;
    const template = this.refs.sizeChip;
    if (!grid || !(template instanceof HTMLTemplateElement)) return;

    grid.replaceChildren();

    for (const [size, label] of readSizes(tile)) {
      // importNode over cloneNode: clones in this document's context, so a custom element
      // in the chip would use this registry.
      const fragment = document.importNode(template.content, true);
      const chip = /** @type {HTMLElement} */ (fragment.firstElementChild);

      chip.dataset.size = size;
      chip.textContent = label || size;
      grid.appendChild(fragment);
    }
  }

  /** @param {HTMLElement | null} tile */
  #updateSizingNote(tile) {
    const note = this.refs.sizingNote;
    if (!note) return;
    const text = tile?.dataset.sizingText ?? '';
    note.textContent = text;
    note.hidden = !text;
  }

  /** Point the drawer's guide at the current style; it fetches its own markup. */
  #updateGuide() {
    const guide = this.refs.sizeGuide;
    const link = this.refs.sizeGuideLink;
    const handle = productHandleFromUrl(this.#style?.dataset.productUrl ?? '');

    if (!guide) return;
    if (!handle) {
      if (link) link.hidden = true;
      return;
    }

    guide.dataset.handle = handle;
  }

  /** @param {Event} event */
  #handleGuideLoaded = (event) => {
    const link = this.refs.sizeGuideLink;

    if (link) link.hidden = !(/** @type {CustomEvent} */ (event).detail?.hasGuide);
  };
}

/**
 * A style tile's size ladder, with its overrides applied.
 *
 * The tile carries the default product's full ladder including sold-out sizes, because a
 * size that product is out of is still in stock on other prints. Hiding one is what
 * `size_overrides` is for.
 *
 * @param {HTMLElement | null} tile
 * @returns {Array<[string, string]>} Size and the label to show for it.
 */
function readSizes(tile) {
  const data = tile?.dataset;
  if (!data) return [];

  const sizes = (data.sizes ?? '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);

  /** @type {Map<string, string>} */
  const overrides = new Map();

  try {
    for (const line of JSON.parse(data.sizeOverrides ?? '[]')) {
      const [size, override] = String(line).split('|');
      if (size != null && override != null) overrides.set(size.trim(), override.trim());
    }
  } catch {
    // A malformed override list should not cost the shopper the size grid.
  }

  return sizes
    .filter((size) => overrides.get(size)?.toLowerCase() !== 'hidden')
    .map((size) => [size, overrides.get(size) ?? size]);
}

if (!customElements.get('product-builder-sizes')) {
  customElements.define('product-builder-sizes', ProductBuilderSizes);
}
